/**
 * 曲一覧のフィルタ設定 BottomSheet
 *
 * アーティスト・曲名・歌詞・配信年・ダウンロード状態での絞り込みを扱う。
 * 歌詞ワードは全文が対象のためサーバー側で、ダウンロード状態は IndexedDB を見てクライアント側で絞り込む。
 */

import { useEffect, useMemo, useState } from 'react'
import Select, { type MultiValue, type StylesConfig } from 'react-select'

import { getMusicArtists } from '@/api/api'
import type { MusicArtistItem } from '@/api/type'
import { Badge } from '@/components/ui/badge'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import {
  type DownloadState,
  EMPTY_SONGS_FILTER,
  type LyricsPresence,
  type LyricsTiming,
  type SongsFilter,
} from './songs-filter'

const LYRICS_PRESENCE_OPTIONS: { value: LyricsPresence; label: string }[] = [
  { value: 'all', label: '全て' },
  { value: 'with', label: '歌詞あり' },
  { value: 'without', label: '歌詞なし' },
]

const LYRICS_TIMING_OPTIONS: { value: LyricsTiming; label: string }[] = [
  { value: 'word', label: '単語同期' },
  { value: 'line', label: '行同期' },
  { value: 'none', label: '同期なし' },
]

const DOWNLOAD_STATE_OPTIONS: { value: DownloadState; label: string }[] = [
  { value: 'all', label: '全て' },
  { value: 'downloaded', label: 'ダウンロード済み' },
  { value: 'not_downloaded', label: '未ダウンロード' },
]

type ArtistOption = {
  label: string
  value: number
}

const selectStyles: StylesConfig<ArtistOption, true> = {
  control: (styles) => ({ ...styles, backgroundColor: 'black', color: 'white' }),
  input: (styles) => ({ ...styles, color: 'white' }),
  menu: (styles) => ({ ...styles, backgroundColor: 'black' }),
  // BottomSheet のスクロール領域でクリップされないよう body にポータルする
  menuPortal: (styles) => ({ ...styles, zIndex: 10001 }),
  multiValue: (styles) => ({ ...styles, backgroundColor: '#333' }),
  multiValueLabel: (styles) => ({ ...styles, color: 'white' }),
  multiValueRemove: (styles) => ({
    ...styles,
    color: 'white',
    ':hover': { backgroundColor: '#555', color: 'white' },
  }),
  option: (styles) => ({
    ...styles,
    backgroundColor: 'black',
    color: 'white',
    ':hover': { ...styles[':hover'], backgroundColor: '#333' },
  }),
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  value: SongsFilter
  onApply: (filter: SongsFilter) => void
}

/** 横並びの排他選択ボタン群 */
const SegmentedControl = <T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[]
  value: T
  onChange: (value: T) => void
}) => (
  <div className="flex overflow-hidden rounded-md border border-white/20">
    {options.map((option, index) => (
      <button
        key={option.value}
        type="button"
        onClick={() => onChange(option.value)}
        className={`flex-1 px-3 py-2! text-xs! transition-colors ${index !== 0 ? 'border-l border-white/20' : ''} ${
          value === option.value ? 'bg-white font-medium text-black' : 'bg-black text-white hover:bg-white/10'
        }`}
      >
        {option.label}
      </button>
    ))}
  </div>
)

export const SongsFilterSheet = ({ open, onOpenChange, value, onApply }: Props) => {
  const [artists, setArtists] = useState<MusicArtistItem[]>([])
  const [draft, setDraft] = useState<SongsFilter>(value)

  // 開くたびに適用済みの条件へ戻す（キャンセルした編集内容を持ち越さない）
  useEffect(() => {
    if (open) setDraft(value)
  }, [open, value])

  useEffect(() => {
    if (!open || artists.length > 0) return
    let cancelled = false
    getMusicArtists()
      .then((res) => {
        if (!cancelled) setArtists(res.artists)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [open, artists.length])

  const artistOptions = useMemo<ArtistOption[]>(
    () => artists.map((artist) => ({ label: artist.name, value: artist.tag_id })),
    [artists],
  )

  const selectedArtistOptions = useMemo(
    () => artistOptions.filter((option) => draft.artistTagIds.includes(option.value)),
    [artistOptions, draft.artistTagIds],
  )

  const handleApply = () => {
    onApply(draft)
    onOpenChange(false)
  }

  const handleClear = () => {
    setDraft(EMPTY_SONGS_FILTER)
    onApply(EMPTY_SONGS_FILTER)
    onOpenChange(false)
  }

  const toggleLyricsTiming = (timing: LyricsTiming, checked: boolean) => {
    setDraft((prev) => ({
      ...prev,
      lyricsTimings: checked ? [...prev.lyricsTimings, timing] : prev.lyricsTimings.filter((t) => t !== timing),
    }))
  }

  const isLyricsDisabled = draft.lyricsPresence === 'without'

  return (
    <BottomSheet title="フィルタ" open={open} onOpenChange={onOpenChange} snapPoints={[0.7, 1]} defaultSnapPoint={0.7}>
      <div className="px-4">
        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1">
            <Badge>アーティスト</Badge>
            <Select<ArtistOption, true>
              isMulti
              options={artistOptions}
              value={selectedArtistOptions}
              placeholder="アーティスト名で絞り込み"
              noOptionsMessage={() => '該当なし'}
              className="w-full"
              menuPlacement="auto"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              maxMenuHeight={240}
              styles={selectStyles}
              onChange={(options: MultiValue<ArtistOption>) =>
                setDraft((prev) => ({ ...prev, artistTagIds: options.map((option) => option.value) }))
              }
            />
          </div>

          <div className="flex flex-col gap-1">
            <Badge>曲名</Badge>
            <Input
              value={draft.title}
              placeholder="曲名に含まれる文字"
              onChange={(e) => setDraft((prev) => ({ ...prev, title: e.target.value }))}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Badge>歌詞</Badge>
            <SegmentedControl
              options={LYRICS_PRESENCE_OPTIONS}
              value={draft.lyricsPresence}
              onChange={(lyricsPresence) => setDraft((prev) => ({ ...prev, lyricsPresence }))}
            />
            <Input
              value={draft.lyricsQuery}
              placeholder="歌詞に含まれるワード（全文検索）"
              disabled={isLyricsDisabled}
              onChange={(e) => setDraft((prev) => ({ ...prev, lyricsQuery: e.target.value }))}
            />
            <div className="flex gap-4">
              {LYRICS_TIMING_OPTIONS.map((option) => (
                <Label key={option.value} className="flex cursor-pointer items-center gap-2">
                  <Checkbox
                    checked={draft.lyricsTimings.includes(option.value)}
                    disabled={isLyricsDisabled}
                    onCheckedChange={(checked) => toggleLyricsTiming(option.value, checked === true)}
                  />
                  <span className="text-sm">{option.label}</span>
                </Label>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Badge>配信年</Badge>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                inputMode="numeric"
                className="w-24"
                placeholder="1990"
                value={draft.releaseYearMin}
                onChange={(e) => setDraft((prev) => ({ ...prev, releaseYearMin: e.target.value }))}
              />
              <span className="text-muted-foreground">-</span>
              <Input
                type="number"
                inputMode="numeric"
                className="w-24"
                placeholder="2026"
                value={draft.releaseYearMax}
                onChange={(e) => setDraft((prev) => ({ ...prev, releaseYearMax: e.target.value }))}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Badge>ダウンロード</Badge>
            <SegmentedControl
              options={DOWNLOAD_STATE_OPTIONS}
              value={draft.downloadState}
              onChange={(downloadState) => setDraft((prev) => ({ ...prev, downloadState }))}
            />
          </div>
        </div>

        <div className="sticky bottom-0 flex items-center gap-2 border-t border-white/10 bg-background py-4">
          <Button variant="outline" className="w-full" onClick={handleClear}>
            クリア
          </Button>
          <Button className="w-full" onClick={handleApply}>
            適用
          </Button>
        </div>
      </div>
    </BottomSheet>
  )
}
